import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { IDEA } from '@/constants/testIds';
import { CATEGORIES, BEST_TIMES } from '@/lib/categories';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function EditIdeaDialog({ idea, open, onOpenChange, onSaved }) {
    const [locationName, setLocationName] = useState('');
    const [category, setCategory] = useState('');
    const [neighborhood, setNeighborhood] = useState('');
    const [note, setNote] = useState('');
    const [bestTime, setBestTime] = useState('');
    const [sourceUrl, setSourceUrl] = useState('');
    const [saving, setSaving] = useState(false);

    // Reset form whenever the dialog opens with a (possibly different) idea
    useEffect(() => {
        if (open && idea) {
            setLocationName(idea.location_name || '');
            setCategory(idea.category || '');
            setNeighborhood(idea.neighborhood || '');
            setNote(idea.note || '');
            setBestTime(idea.best_time || '');
            setSourceUrl(idea.source_url || '');
        }
    }, [open, idea]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!locationName.trim()) {
            toast.error('Location name is required');
            return;
        }
        if (!category) {
            toast.error('Pick a category');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                location_name: locationName.trim(),
                category,
                neighborhood: neighborhood.trim() || null,
                note: note,
                best_time: bestTime || null,
                source_url: sourceUrl.trim() || null,
            };
            const { data } = await api.patch(`/ideas/${idea.inspiration_id}`, payload);
            toast.success('Idea updated');
            onSaved?.(data.idea);
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not save changes');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={IDEA.editDialog} onClick={(e) => e.stopPropagation()} className="sm:max-w-md rounded-3xl border-[var(--ns-border)]">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tight">Edit idea</DialogTitle>
                    <DialogDescription className="text-sm text-[var(--ns-text2)]">
                        Update the details for this idea. Other group members will see the changes immediately.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-3 mt-1">
                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Location name</label>
                        <Input
                            data-testid={IDEA.editLocationInput}
                            value={locationName}
                            onChange={(e) => setLocationName(e.target.value)}
                            maxLength={140}
                            className="rounded-xl border-[var(--ns-border)] bg-white h-11"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Category</label>
                            <Select value={category} onValueChange={setCategory}>
                                <SelectTrigger data-testid={IDEA.editCategorySelect} className="h-11 rounded-xl border-[var(--ns-border)] bg-white">
                                    <SelectValue placeholder="Pick one" />
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl">
                                    {CATEGORIES.map((c) => {
                                        const Icon = c.icon;
                                        return (
                                            <SelectItem key={c.value} value={c.value}>
                                                <span className="inline-flex items-center gap-2">
                                                    <Icon size={14} style={{ color: c.color }} /> {c.label}
                                                </span>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Best time</label>
                            <Select value={bestTime || 'none'} onValueChange={(v) => setBestTime(v === 'none' ? '' : v)}>
                                <SelectTrigger data-testid={IDEA.editBestTimeSelect} className="h-11 rounded-xl border-[var(--ns-border)] bg-white">
                                    <SelectValue placeholder="Anytime" />
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl">
                                    <SelectItem value="none">— Not set —</SelectItem>
                                    {BEST_TIMES.map((b) => (
                                        <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Neighborhood / area</label>
                        <Input
                            data-testid={IDEA.editNeighborhoodInput}
                            value={neighborhood}
                            onChange={(e) => setNeighborhood(e.target.value)}
                            maxLength={120}
                            placeholder="e.g. Bairro Alto, Lisbon"
                            className="rounded-xl border-[var(--ns-border)] bg-white h-11"
                        />
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Your note</label>
                        <Textarea
                            data-testid={IDEA.editNoteTextarea}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            maxLength={500}
                            rows={3}
                            placeholder="Why is this on the list?"
                            className="rounded-xl border-[var(--ns-border)] bg-white"
                        />
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">Source URL</label>
                        <Input
                            data-testid={IDEA.editSourceUrlInput}
                            value={sourceUrl}
                            onChange={(e) => setSourceUrl(e.target.value)}
                            placeholder="https://…"
                            className="rounded-xl border-[var(--ns-border)] bg-white h-11"
                        />
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                        <Button
                            type="submit"
                            disabled={saving}
                            data-testid={IDEA.editSubmitButton}
                            className="rounded-full h-10 px-5 text-white"
                            style={{ background: 'var(--ns-navy)' }}
                        >
                            {saving ? <><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</> : 'Save changes'}
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            data-testid={IDEA.editCancelButton}
                            className="rounded-full h-10 px-4"
                        >
                            Cancel
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
